import type { PluginKind } from '@crowdex/agent-contracts/api';
import type { CrowdexManifest } from './crowdex-manifest';

export { PLUGIN_KINDS, type PluginKind } from '@crowdex/agent-contracts/api';

/**
 * The contracts of a model or voice provider. A plugin that provides only these is wiring: the
 * organization sets models up in Crowpus (ADR 0004), Settings › Models and Voice choose them.
 */
export const MODEL_CONTRACTS: ReadonlySet<string> = new Set(['inference.v1', 'realtime.v1']);

/** The parts of a `crowdex.json` its kind is read from. */
export type PluginKindSignals = Partial<
  Pick<
    CrowdexManifest,
    | 'service'
    | 'page'
    | 'types'
    | 'agents'
    | 'mcp'
    | 'connections'
    | 'tools'
    | 'skills'
    | 'actions'
    | 'widgets'
  >
> & { provides?: readonly { contract: string }[] | undefined };

const some = (list: readonly unknown[] | undefined) => (list?.length ?? 0) > 0;

/**
 * What a plugin is, from what its manifest brings (kinds are derived, never declared: R5 of
 * `docs/adr-plugin-model-uniformization.md`). In order, the first that holds:
 *
 * - `app`: a Crowlingo application (`service`), or a page without artifact types;
 * - `editor`: a page that opens artifact types;
 * - `provider`: it only provides model or voice contracts;
 * - `agent`: it only brings coding agents;
 * - `service`: a connection, and what acts with it (contracts, MCP servers, tools);
 * - `producer`: artifact types and no page (a common viewer reads them);
 * - `skill`: skills alone.
 */
export function pluginKind(manifest: PluginKindSignals): PluginKind {
  const provides = manifest.provides ?? [];
  if (manifest.service !== undefined) return 'app';
  if (manifest.page !== undefined) return some(manifest.types) ? 'editor' : 'app';
  const acts = some(manifest.mcp) || manifest.tools === true;
  if (
    provides.length > 0 &&
    provides.every((entry) => MODEL_CONTRACTS.has(entry.contract)) &&
    !acts &&
    !some(manifest.agents)
  )
    return 'provider';
  if (some(manifest.agents) && provides.length === 0 && !acts && !some(manifest.connections))
    return 'agent';
  if (some(manifest.connections) && (provides.length > 0 || acts)) return 'service';
  if (some(manifest.types)) return 'producer';
  if (manifest.skills !== undefined && !acts && !some(manifest.actions)) return 'skill';
  return 'plugin';
}

/** Wiring the user never manages as a plugin: model and voice providers, coding agents. */
export function isPluginKindHidden(kind: PluginKind | undefined): boolean {
  return kind === 'provider' || kind === 'agent';
}

/**
 * Whether a plugin is one of All apps' (and the favourites'): an app or an editor, with a page to
 * open. A plugin without a page has nothing to open there, whatever its kind; services, providers,
 * agents, skills and producers are reached from Settings › Plugins and ⌘K instead. The one place
 * both rules meet: extend it rather than filter All apps elsewhere.
 */
export function isListedInAllApps(
  /** A manifest, or a plugin whose kind is already derived (`kind` of the listing). */
  plugin: PluginKindSignals | { kind: PluginKind | undefined; page?: unknown },
): boolean {
  if (plugin.page === undefined || plugin.page === null) return false;
  const kind = 'kind' in plugin ? plugin.kind : pluginKind(plugin);
  return kind === undefined || kind === 'app' || kind === 'editor';
}

/** Whether Settings › Plugins (and ⌘K) list it: every plugin but hidden wiring. */
export function isListedInPlugins(plugin: { kind?: PluginKind | undefined }): boolean {
  return !isPluginKindHidden(plugin.kind);
}
